export type PortalRole = 'ndi:broker' | 'ndi:customer';

export const ROLE_LABELS: Record<PortalRole, string> = { 'ndi:customer': 'Customer', 'ndi:broker': 'Broker' };

// Shown in place of a user's name while the identity mirror has not caught up with them.
export const PENDING = 'Pending — ready in about 15 minutes';

export type PortalUser = {
  userId: number;
  // email and fullName are null when the identity mirror has no account for the user; role is null when it has no portal role.
  email: string | null;
  fullName: string | null;
  role: PortalRole | null;
  active: boolean;
  // Every customer the user is granted, the one on screen included, ordered by name.
  customers: { customerId: string; name: string }[];
  firstGrantedAt: string | null;
};

/* The mirror lags Kalidasa by about 15 minutes. A grant older than this whose account or role the mirror still lacks
   was made outside portal user management (a hand-made grant, or an account since removed), and never catches up. */
const MIRROR_LAG_MS = 60 * 60 * 1000;

export type PortalUserState = 'ready' | 'pending' | 'noPortalRole' | 'noAccount';

export const portalUserState = (user: PortalUser, now: number = Date.now()): PortalUserState => {
  if (user.role !== null && user.email !== null) {
    return 'ready';
  }
  const granted = user.firstGrantedAt === null ? Number.NaN : Date.parse(user.firstGrantedAt);
  if (now - granted < MIRROR_LAG_MS) {
    return 'pending';
  }
  return user.email === null ? 'noAccount' : 'noPortalRole';
};

// The create answer: the user, and for a new account only, the temporary password to send them. It is shown once.
export type CreatedPortalUser = PortalUser & { temporaryPassword: string | null };

export type CustomerChoice = { customerId: string; name: string; nickname?: string | null };

/* A rejected API call throws the axios response object ({ status, data }), not an Error, and the
   data carries the server's { error, message } body. */
export const errorCode = (err: unknown): string | undefined => (err as { data?: { error?: string } } | null)?.data?.error;

export const errorText = (err: unknown, fallback: string): string => {
  const data = (err as { data?: { message?: unknown } } | null)?.data;
  return typeof data?.message === 'string' && data.message ? data.message : fallback;
};

type SearchConfig = {
  limit?: number | null;
  offset?: number | null;
  filters?: { field: string; value?: unknown; operator?: string }[];
  sort?: { field?: string; by?: string };
};

const PORTAL_USER_FILTERS = ['q', 'role', 'status', 'customerId'];

/* Translates Siege's search config into the query string of GET /portal-users, leading '?' included; '' when there is nothing to send. */
export const portalUserQuery = (searchConfig?: SearchConfig | null): string => {
  const params = new URLSearchParams();
  for (const { field, value } of searchConfig?.filters ?? []) {
    if (PORTAL_USER_FILTERS.includes(field) && value !== undefined && value !== null && String(value) !== '') {
      params.set(field, String(value));
    }
  }
  const sort = searchConfig?.sort;
  if (sort?.field) {
    params.set('sort', sort.field);
    if (sort.by) {
      params.set('direction', sort.by.toLowerCase());
    }
  }
  const { offset, limit } = searchConfig ?? {};
  if (typeof offset === 'number') {
    params.set('offset', String(offset));
  }
  if (typeof limit === 'number') {
    params.set('limit', String(limit));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};
